import { findIndex } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useLocalStorage } from 'react-use';
import { MALE, NON_SMOKER } from '../constants';
import {
  AttributeVariable,
  Product,
  ProductGroup,
  ProductVariable,
  Promotion
} from '../graphql';
import { getProductList, getProductListAttributes } from '../helpers';
import { getAllQueryParams } from '../helpers/queryparam';
import { useListProductGroup } from '../hooks';
import { getSafeProductType } from '../services/productMeta';

const QUOTATION_PARAMS_KEY = 'quotationParams';

const getParamsFromLocalStorage = () => {
  const quotationParamsStr = localStorage.getItem(QUOTATION_PARAMS_KEY);
  if (!quotationParamsStr) return {};
  try {
    const quotationParams = JSON.parse(quotationParamsStr);
    return quotationParams || {};
  } catch (error) {
    return {};
  }
};

const INITIAL_QUOTATION_FORM = {
  gender: MALE,
  smoker: NON_SMOKER,
  age: 25,
  code: '',
  defaultOption: 1,
  sumAssured: 1000000,
  ...getParamsFromLocalStorage(),
};

type QuotationForm = {
  gender: 'male' | 'female';
  smoker: 'smoker' | 'non-smoker';
  age: number;
  code: string;
  defaultOption: string;
  sumAssured: number;
};

type QuotationContextValue = {
  values: QuotationForm;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  style: string;
  coupon: Promotion | undefined;
  setCoupon: (promotion: Promotion | undefined) => void;
  validateIssueAge: (product: Product | null, age: number) => boolean;
  isSumAssuredValid: boolean;
  setIsSumAssuredValid: (arg1: boolean) => void;
  productType: string;
  activeProduct: Product | null;
  setActiveProduct: (arg1: Product) => void;
  selectedVariable: AttributeVariable;
  setSelectedVariable: (arg1: AttributeVariable) => void;
  productGroups: ProductGroup[];
  products: Product[];
  variables: ProductVariable[];
};

export const QuotationContext: React.Context<QuotationContextValue> =
  createContext({});

export const useQuotationContext = () => {
  return useContext(QuotationContext);
};

export const QuotationProvider: React.FC = ({ children }) => {
  const { type, style, defaultCoupon: code, ...qs } = getAllQueryParams();
  const [values, setValues] = useLocalStorage(
    QUOTATION_PARAMS_KEY,
    { ...INITIAL_QUOTATION_FORM, code },
    {
      raw: false,
      // Not saving coupon code
      serializer: ({ age, gender, smoker, sumAssured }) =>
        JSON.stringify({ age, gender, smoker, sumAssured }),
      deserializer: serialized => JSON.parse(serialized),
    },
  );

  const productType = getSafeProductType(type);
  const [coupon, setCoupon] = React.useState<Promotion>();
  const [activeProduct, setActiveProduct] = React.useState<Product | null>(
    null,
  );

  const [isSumAssuredValid, setIsSumAssuredValid] = useState(true);
  const [selectedVariable, setSelectedVariable] =
    useState<AttributeVariable | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const key = event.target.name;

      const numberType = ['age', 'sumAssured'];

      const value = numberType.includes(key)
        ? parseInt(event.target.value, 10)
        : event.target.value;
      const params = {
        ...values,
        [key]: value,
      };
      setValues(params);
    },
    [setValues, values],
  );

  const validateIssueAge = (product, age) => {
    const {
      min_issue_age_value: minIssueAge,
      min_issue_age_unit: minIssueAgeUnit,
      max_issue_age_value: maxIssueAge,
    } = product || {};
    const isIssueAgeValid =
      (minIssueAgeUnit === 'days' ? 0 : minIssueAge) <= age &&
      age <= maxIssueAge;
    return isIssueAgeValid;
  };

  const [productGroups] = useListProductGroup({
    type: productType,
    variableAttribute: qs.variableAttribute,
  });

  const [products, variables] = useMemo(() => {
    if (productGroups) {
      return [
        getProductList(productGroups),
        getProductListAttributes(productGroups),
      ];
    } else {
      return [undefined, undefined];
    }
  }, [productGroups]);

  useEffect(() => {
    if (variables && !selectedVariable) {
      const variable = variables.find(
        v => v.attribute.key === qs.variableAttribute,
      );
      // TODO: allow users to customize default value
      setSelectedVariable(variable ? variable.values[qs.defaultVariable ?? 0] : null);
    }
  }, [variables, qs.variableAttribute, qs.defaultVariable, selectedVariable]);

  // TODO: split into grouped vs not-grouped modes and separate logic accordingly
  useEffect(() => {
    if (productGroups) {
      let nextProduct: Product | null;
      const coerced = Number(qs.defaultOption);
      const ix = Number.isNaN(coerced) ? 0 : coerced;
      if (productGroups.length === 1) {
        // Not to reinitialize if there's already a selected product
        if (activeProduct) {
          nextProduct = activeProduct;
        } else {
          const elementIx = ix;
          const elements = productGroups[0].elements;
          nextProduct = elements[elementIx].resource;
        }
      } else if (selectedVariable) {
        // Align group index with the currently selected product if one exists
        const groupIx = activeProduct
          ? findIndex(productGroups, {
              elements: [{ resource: { code: activeProduct.code } }],
            })
          : ix;
        const group = productGroups[groupIx];
        const element = group.elements.find(el =>
          el.variable_ids.includes(selectedVariable.id),
        );
        nextProduct = element.resource;
      } else {
        nextProduct = null;
      }
      setActiveProduct(nextProduct);
    }
  }, [activeProduct, productGroups, qs.defaultOption, selectedVariable]);

  const value = useMemo(
    () => ({
      values,
      handleChange,
      style,
      coupon,
      setCoupon,
      validateIssueAge,
      isSumAssuredValid,
      setIsSumAssuredValid,
      productType,
      activeProduct,
      setActiveProduct,
      selectedVariable,
      setSelectedVariable,
      productGroups,
      products,
      variables,
    }),
    [
      activeProduct,
      variables,
      coupon,
      handleChange,
      isSumAssuredValid,
      productGroups,
      productType,
      products,
      selectedVariable,
      style,
      values,
    ],
  );

  return (
    <QuotationContext.Provider value={value}>
      {children}
    </QuotationContext.Provider>
  );
};
